/**
 * EmpiriVita Cart Drawer Subscription Upsell
 * -------------------------------------------
 * Collapsed accordion: subtle "Want to save 10%?" prompt that
 * the customer taps to expand, selects dog weight, then subscribes.
 *
 * Uses Shopify's AJAX Cart API with FormData format:
 *   - /cart/change.js with selling_plan to ADD a subscription
 *   - /cart/change.js with selling_plan="" to REMOVE a subscription
 *
 * CRITICAL: Must use FormData (not JSON) — Appstle intercepts JSON
 * requests and can silently strip the selling_plan parameter.
 * Single /cart/change.js call — no remove-and-re-add needed.
 */

(function () {
  'use strict';

  /* ── Selectors ──────────────────────────────────────── */

  const SEL = {
    accordion:        '.ev-sub-accordion',
    accordionTrigger: '.ev-sub-accordion__trigger',
    accordionPanel:   '.ev-sub-accordion__panel',
    accordionSelect:  '.ev-sub-accordion__select',
    accordionBtn:     '.ev-sub-accordion__btn',

    activeContainer:  '.ev-sub-active',
    activeSelect:     '.ev-sub-active__select',
    activeChangeBtn:  '.ev-sub-active__change',
    activeRemoveBtn:  '.ev-sub-active__remove',

    cartDrawer: '#cart-drawer',
  };

  /* ── Cart API ───────────────────────────────────────── */

  /**
   * Change the selling plan on an existing cart line item.
   * Uses FormData format — required for Appstle compatibility.
   *
   * @param {string} lineKey - The line item key from cart
   * @param {string|null} sellingPlanId - Plan ID to apply, or null/empty to remove
   */
  async function changeSellingPlan(lineKey, sellingPlanId) {
    const fd = new FormData();
    fd.append('id', lineKey);
    fd.append('selling_plan', sellingPlanId ? String(sellingPlanId) : '');

    const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      body: fd,
    });

    if (!res.ok) throw new Error('Cart change failed: ' + res.status);
    return res.json();
  }

  /* ── Cart drawer refresh ────────────────────────────── */

  async function refreshCartDrawer() {
    try {
      const res = await fetch(window.Shopify.routes.root + '?sections=cart-drawer');
      const sections = await res.json();

      if (sections['cart-drawer']) {
        const drawer = document.querySelector(SEL.cartDrawer);
        if (drawer) {
          const temp = document.createElement('div');
          temp.innerHTML = sections['cart-drawer'];
          const newDrawer = temp.querySelector(SEL.cartDrawer);
          if (newDrawer) {
            drawer.innerHTML = newDrawer.innerHTML;
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            return;
          }
        }
      }
      window.location.reload();
    } catch (err) {
      console.error('[EmpiriVita] Cart refresh failed:', err);
      window.location.reload();
    }
  }

  /* ── Button state helper ────────────────────────────── */

  function setLoading(btn, isLoading, defaultText) {
    if (isLoading) {
      btn.disabled = true;
      btn.dataset.originalText = btn.textContent;
      btn.textContent = 'Switching…';
      btn.classList.add('ev-loading');
    } else {
      btn.disabled = false;
      btn.textContent = defaultText || btn.dataset.originalText;
      btn.classList.remove('ev-loading');
    }
  }

  /* ── Event handlers ─────────────────────────────────── */

  function handleClick(e) {

    /* ── Accordion trigger (expand/collapse) ── */
    const trigger = e.target.closest(SEL.accordionTrigger);
    if (trigger) {
      const accordion = trigger.closest(SEL.accordion);
      if (!accordion) return;

      const panel = accordion.querySelector(SEL.accordionPanel);
      if (!panel) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isOpen);
      panel.hidden = isOpen;

      const arrow = trigger.querySelector('.ev-sub-accordion__arrow');
      if (arrow) {
        arrow.classList.toggle('ev-sub-accordion__arrow--open', !isOpen);
      }
      return;
    }

    /* ── "Subscribe & Save" button ── */
    const subBtn = e.target.closest(SEL.accordionBtn);
    if (subBtn) {
      const accordion = subBtn.closest(SEL.accordion);
      if (!accordion) return;

      const select = accordion.querySelector(SEL.accordionSelect);
      const planId = select ? select.value : null;
      if (!planId) return;

      const { lineKey } = accordion.dataset;
      setLoading(subBtn, true);

      changeSellingPlan(lineKey, planId)
        .then(() => refreshCartDrawer())
        .catch((err) => {
          console.error('[EmpiriVita] Subscribe failed:', err);
          setLoading(subBtn, false, 'Subscribe & Save 10%');
        });
      return;
    }

    /* ── "Update" button (change subscription plan) ── */
    const changeBtn = e.target.closest(SEL.activeChangeBtn);
    if (changeBtn) {
      const container = changeBtn.closest(SEL.activeContainer);
      if (!container) return;

      const select = container.querySelector(SEL.activeSelect);
      const planId = select ? select.value : null;
      if (!planId) return;

      const { lineKey } = container.dataset;
      setLoading(changeBtn, true);

      changeSellingPlan(lineKey, planId)
        .then(() => refreshCartDrawer())
        .catch((err) => {
          console.error('[EmpiriVita] Plan change failed:', err);
          setLoading(changeBtn, false, 'Update');
        });
      return;
    }

    /* ── "Switch to One-Time" button ── */
    const removeBtn = e.target.closest(SEL.activeRemoveBtn);
    if (removeBtn) {
      const container = removeBtn.closest(SEL.activeContainer);
      if (!container) return;

      const { lineKey } = container.dataset;
      setLoading(removeBtn, true);

      changeSellingPlan(lineKey, null)
        .then(() => refreshCartDrawer())
        .catch((err) => {
          console.error('[EmpiriVita] One-time switch failed:', err);
          setLoading(removeBtn, false, 'Switch to One-Time');
        });
    }
  }

  function handleChange(e) {
    if (e.target.matches(SEL.accordionSelect)) {
      const accordion = e.target.closest(SEL.accordion);
      const btn = accordion ? accordion.querySelector(SEL.accordionBtn) : null;
      if (btn) btn.disabled = !e.target.value;
    }

    if (e.target.matches(SEL.activeSelect)) {
      const container = e.target.closest(SEL.activeContainer);
      const btn = container ? container.querySelector(SEL.activeChangeBtn) : null;
      if (btn) btn.disabled = false;
    }
  }

  /* ── Initialize ─────────────────────────────────────── */

  document.addEventListener('click', handleClick);
  document.addEventListener('change', handleChange);
})();